<template>
  <div class="toxprints-chemicals-view">
    <v-data-table
      v-if="filteredItems.length"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :hide-default-footer="!items || filteredItems.length <= 10"
      :headers="headers"
      :items="filteredItems"
      :page.sync="_currentPage"
      :items-per-page.sync="_itemsPerPage"
    >
      <template #[`item.toxprint`]="{ item }">
        <div
          :id="`a-1-${item.ordinal}`"
          style="height: 100%"
        >
          <div class="d-flex flex-column align-start">
            <img
              v-if="showToxprintStructures"
              :src="`${TOXPRINTS_URL}/render?id=${item.ordinal}&profile=${profile}`"
              alt=""
              :title="item.label"
              v-b-tooltip
              style="width: 150px; height: 150px"
            />

            <span
              :id="`txp-${item.ordinal}`"
              style="white-space: nowrap"
            >
              {{ item.label }}
            </span>

            <b-popover
              v-if="!showToxprintStructures"
              :target="`txp-${item.ordinal}`"
              triggers="hover"
            >
              <img
                :src="`${TOXPRINTS_URL}/render?id=${item.ordinal}&profile=${profile}`"
                alt=""
              />
            </b-popover>
          </div>
        </div>
      </template>

      <template #[`item.CTE`]="{ item }">
        <v-chip
          v-if="item.CTE"
          color="green"
          small
          title="Enriched"
          v-b-tooltip
        />
      </template>

      <template
        v-for="h in mainMetricsHeaders"
        #[`item.${h.value}`]="{ item }"
      >
        <div
          :key="`item-${h.value}-${item.id}`"
          style="height: 100%; margin-top: 1em"
        >
          <div v-if="item[h.value] != null && floatFields.includes(h.value)">
            {{ item[h.value] | number('0.00') }}
          </div>
          <div v-else>{{ item[h.value] }}</div>
        </div>
      </template>

      <template
        v-for="h in [{ value: 'CTE' }, ...metricsHeaders]"
        #[`header.${h.value}`]="{ header }"
      >
        <span
          :key="`header-${h.value}`"
          :title="getTitle(h.value)"
          v-b-tooltip
        >
          {{ header.text }}
        </span>
      </template>

      <template #[`item.structures`]="{ item }">
        <div
          v-for="(rec, i) in ordinal2chemicals(item.ordinal)"
          :key="`${item.ordinal}-${chemicalId(rec)}-${i}`"
          style="display: inline-block; position: relative"
          :class="['shadered-on-hover', { 'selected-chemical-card': isSelectedChemical(rec) }]"
        >
          <img
            :key="`img-${item.ordinal}-${chemicalId(rec)}-${i}`"
            :id="`${item.ordinal}-${chemicalId(rec)}-${i}`"
            :src="`${TOXPRINTS_URL}/render?smiles=${encodeURIComponent(rec.smiles)}`"
            :alt="`Chemical structure of ${encodeURIComponent(rec.smiles) || 'chemical'}`"
            style="width: 150px; height: 150px"
            @click="$emit('navigate', 0, chemicalId(rec))"
          />
          <v-icon
            v-if="isInDSSTox(rec)"
            class="top-right"
            color="green"
            aria-label="Present in DSSTox database"
          >
            mdi-database-check
          </v-icon>
          <div
            v-if="showNames"
            style="font-size: smaller"
          >
            <div
              v-for="prop in ['cid', 'sid', 'casrn', 'name']"
              :key="`org-${prop}`"
              class="ml-1 mb-1 text-truncate-150"
            >
              <a
                v-if="['cid', 'sid', 'casrn'].includes(prop)"
                :href="`https://comptox.epa.gov/dashboard/dsstoxdb/results?search=${rec[prop]}`"
                target="epa"
              >
                {{ rec[prop] }}
              </a>
              <span v-else>
                {{ rec[prop] }}
              </span>
            </div>
          </div>
          <ChemPopover
            :chemical="rec"
            :target="`${item.ordinal}-${chemicalId(rec)}-${i}`"
            source="toxprints"
          />
        </div>
      </template>
    </v-data-table>

    <div
      v-else
      class="mt-15 text-center"
    >
      {{ emptyStateMessage }}
    </div>
  </div>
</template>

<script>
import { TOXPRINTS_URL } from '@/main'
import ChemPopover from '@/components/chemical/ChemPopover/ChemPopover.vue'

export default {
  components: { ChemPopover },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    chemicals: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    profile: {
      type: String,
      default: null,
    },
    metrics: {
      type: Array,
      default: () => [],
    },
    selectedChemicalKey: {
      type: [String, Number],
      default: null,
    },
    showNames: {
      type: Boolean,
      default: false,
    },
    showToxprintStructures: {
      type: Boolean,
      default: false,
    },
    showStructures: {
      type: Boolean,
      default: false,
    },
    showOtherMetrics: {
      type: Boolean,
      default: false,
    },
    showEnrichedOnly: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 30,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
  },

  data: () => ({
    baseHeaders: [{ text: 'ToxPrint', value: 'toxprint', sortable: false }],
    mainMetricsHeaders: [
      { text: 'OR', value: 'OR' },
      { text: 'pval', value: 'PV1' },
      { text: 'TP', value: 'TP' },
      { text: 'FN', value: 'FN' },
      { text: 'FP', value: 'FP' },
      { text: 'TN', value: 'TN' },
      { text: 'BA', value: 'BA' },
    ],
    otherMetricsHeaders: [
      { text: 'Count', value: 'count' },
      { text: 'PV1', value: 'PV1' },
      { text: 'TPR', value: 'TPR' },
      { text: 'TNR', value: 'TNR' },
      { text: 'PPV', value: 'PPV' },
      { text: 'NPV', value: 'NPV' },
      { text: 'FNR', value: 'FNR' },
      { text: 'FPR', value: 'FPR' },
      { text: 'FDR', value: 'FDR' },
      { text: 'FOR', value: 'FOR' },
      { text: 'PT', value: 'PT' },
      { text: 'TS', value: 'TS' },
      { text: 'ACC', value: 'ACC' },
      { text: 'F1', value: 'F1' },
      { text: 'MCC', value: 'MCC' },
      { text: 'FM', value: 'FM' },
      { text: 'BM', value: 'BM' },
      { text: 'MK', value: 'MK' },
    ],
    floatFields: [
      'BA',
      'OR',
      'PV1',
      'PV2',
      'TPR',
      'TNR',
      'PPV',
      'NPV',
      'FNR',
      'FPR',
      'FDR',
      'FOR',
      'PT',
      'TS',
      'ACC',
      'F1',
      'MCC',
      'FM',
      'BM',
      'MK',
    ],
  }),

  computed: {
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    headers() {
      let headers = [...this.baseHeaders]
      if (this.hasActivities) {
        if (this.hasEnriched) headers.push({ text: 'CTE', value: 'CTE' })
        headers.push(...this.mainMetricsHeaders)
      }
      if (this.showOtherMetrics) headers.push(...this.otherMetricsHeaders)
      if (this.showStructures)
        headers.push({
          text: 'Structures',
          value: 'structures',
          sortable: false,
          width: '90%',
        })
      return headers
    },
    metricsHeaders() {
      return [...this.mainMetricsHeaders, ...this.otherMetricsHeaders]
    },
    hasActivities() {
      return !!this.chemicals.find((r) => r.activity)
    },
    hasEnriched() {
      return !!this.items.find((r) => r.CTE)
    },
    filteredItems() {
      return this.showEnrichedOnly ? this.items.filter((i) => i.CTE) : this.items
    },
    emptyStateMessage() {
      if (!this.items.length) {
        return 'No data to display'
      }

      if (this.showEnrichedOnly && !this.filteredItems.length) {
        return "No enriched toxprints found. Toggle 'Enriched' to view all rows."
      }

      return 'No data to display'
    },
    _itemsPerPage: {
      get() {
        return this.itemsPerPage
      },
      set(value) {
        this.$emit('update:itemsPerPage', value)
      },
    },
    _currentPage: {
      get() {
        return this.currentPage
      },
      set(value) {
        this.$emit('update:currentPage', value)
      },
    },
  },

  methods: {
    chemicalId(chemical) {
      return chemical?.id ?? chemical?.chemId ?? chemical?.sid ?? chemical?.key
    },
    isSelectedChemical(chemical) {
      return this.selectedChemicalKey != null && this.chemicalId(chemical) === this.selectedChemicalKey
    },
    getTitle(header) {
      const metric = this.metrics.find((m) => m.id === header)
      return metric ? metric.description : null
    },
    isInDSSTox(item) {
      return item.cid || item.sid || item.name || item.casrn
    },
    ordinal2chemicals(ordinal) {
      let chemicals = []
      for (let i = 0; i < this.chemicals.length; i++) {
        if (this.chemicals[i].bits && this.chemicals[i].bits[ordinal]) chemicals.push(this.chemicals[i])
      }
      return chemicals
    },
  },
}
</script>

<style scoped>
.toxprints-chemicals-view {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.v-data-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

::v-deep .v-data-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

::v-deep .v-data-table__wrapper thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

::v-deep .v-data-footer {
  flex: 0 0 auto;
}

.top-right {
  position: absolute;
  top: 4px;
  right: 4px;
}

.selected-chemical-card {
  border-radius: 8px;
  box-shadow: 0 0 0 3px rgba(24, 115, 205, 0.9);
  background: rgba(24, 115, 205, 0.08);
}
</style>
